import { lazy, Suspense } from "react";
import { UserProvider, useUser } from "./components/UserContext";
import LoginPage from "./components/LoginPage";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import HomePage from "./components/HomePage";
import AgentListPage from "./components/AgentListPage";
import AgentDetailPage from "./components/AgentDetailPage";
import NewAgentDetailPage from "./components/NewAgentDetailPage";
import NewAgentListPage from "./components/NewAgentListPage";
import NewAgentPage from "./components/NewAgentPage";
import ConversationalAgentCreator from "./components/ConversationalAgentCreator";
import ConfigPage from "./components/ConfigPage";
import BrandingConfigPage from "./components/BrandingConfigPage";
import NeedlepathDashboard from "./components/NeedlepathDashboard";
import MnemoDashboard from "./components/MnemoDashboard";
import { CodeIndexerPage, DocumentIndexerPage } from "./components/IndexerPages";
import KnowledgeBuilderListPage from "./components/KnowledgeBuilderListPage";
import KnowledgeBuilderDetailPage from "./components/KnowledgeBuilderDetailPage";
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext';
import { TenantBrandingProvider, useTenantBranding } from "./context/TenantBrandingContext";
import Dashboard from "./components/Dashboard";
import AuditLogsPage from "./components/AuditLogsPage.jsx";
import StepperWizard from "./components/AgentCreate/StepperWizard";
import AddUserPage from "./components/AddUserPage";
import ToolCatalog from "./components/Tools/ToolCatalog";
import ToolDetailPage from "./components/Tools/ToolDetailPage";
import KeyManager from "./components/KeyManager";
import WorkflowGeneratorPage from "./components/Workflows/WorkflowGeneratorPage";
import WorkflowEditPage from "./components/Workflows/WorkflowEditPage";
import WorkflowDetailPage from "./components/Workflows/WorkflowDetailPage";
import WorkflowRunPage from "./components/Workflows/WorkflowRunPage";
import WorkflowListPage from "./components/Workflows/WorkflowListPage";
import ApprovalsListPage from "./components/Workflows/ApprovalsListPage";
import ApprovalDetailPage from "./components/Workflows/ApprovalDetailPage";
import UserListPage from "./components/UserListPage";
import AgentRunPage from "./components/AgentRunPage";
import IntentToAgentCreator from "./components/AgentCreate/IntentToAgentCreator";
import IntentToWorkflowCreator from "./components/Workflows/IntentToWorkflowCreator";
import CasperWorkspace from "./components/Casper/CasperWorkspace.jsx";
import { NextMocaThemeProvider } from "./ThemeModeContext";
import RolesPermissionsPage from "./components/RolesPermissionsPage";
import { PERMISSIONS } from "./permissions";
import { PLATFORM_TELEMETRY_PERMISSIONS } from "./rbacUiPolicy";
import './styles.css';

const ToolForgeApp = lazy(() => import("./components/ToolForge/ToolForgeApp.jsx"));

function RequireAuth({ children }) {
  const { user } = useUser();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function RequirePermission({ permission, anyOf, children }) {
  const { user, can, canAny } = useUser();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  const allowed = permission ? can(permission) : canAny(anyOf || []);
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function DefaultLanding() {
  const { canAny } = useUser();
  if (!canAny([PERMISSIONS.AGENTS_READ, PERMISSIONS.WORKFLOWS_READ, PERMISSIONS.TOOLS_READ, PERMISSIONS.USERS_READ, PERMISSIONS.ROLES_READ, PERMISSIONS.AUDIT_READ])) {
    return <Navigate to="/casper" replace />;
  }
  return <HomePage />;
}

function AppRoutes() {
  const { login, canAny } = useUser();
  const { flags } = useFeatureFlags();
  const canUseCreateTool = flags.enableCreateTool && canAny([PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE]);
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={login} />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<RequireAuth><DefaultLanding /></RequireAuth>} />
        <Route path="casper" element={<RequireAuth><CasperWorkspace /></RequireAuth>} />
        <Route path="agent/new" element={<RequirePermission permission={PERMISSIONS.AGENTS_CREATE}><NewAgentPage /></RequirePermission>} />
        {flags.newAgentPage ? 
          (<>
          <Route path="agent/:agentId" element={<RequirePermission permission={PERMISSIONS.AGENTS_READ}><NewAgentDetailPage /></RequirePermission>} />
          <Route path="agent/:agentId/chatAgent" element={<RequirePermission permission={PERMISSIONS.AGENTS_RUN}><AgentRunPage /></RequirePermission>} />
          <Route path="dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
           <Route path="agent/conversational" element={<RequirePermission permission={PERMISSIONS.AGENTS_CREATE}><StepperWizard /></RequirePermission>} />
           
          <Route path="agents" element={<RequirePermission permission={PERMISSIONS.AGENTS_READ}><NewAgentListPage /></RequirePermission>} />
          <Route path="tools" element={<RequirePermission permission={PERMISSIONS.TOOLS_READ}><ToolCatalog /></RequirePermission>} />
          <Route path="tool/:toolId" element={<RequirePermission permission={PERMISSIONS.TOOLS_READ}><ToolDetailPage /></RequirePermission>} />
          </>) :
          (<>
            <Route path="agent/:agentId" element={<RequirePermission permission={PERMISSIONS.AGENTS_READ}><AgentDetailPage /></RequirePermission>} />
            <Route path="agents" element={<RequirePermission permission={PERMISSIONS.AGENTS_READ}><AgentListPage /></RequirePermission>} />
             <Route path="agent/conversational" element={<RequirePermission permission={PERMISSIONS.AGENTS_CREATE}><ConversationalAgentCreator /></RequirePermission>} />
          </>)
        }
        <Route path="agent/create" element={<RequirePermission permission={PERMISSIONS.AGENTS_CREATE}><IntentToAgentCreator /></RequirePermission>} />
        <Route
          path="tool-forge"
          element={
            <RequirePermission anyOf={[PERMISSIONS.TOOLS_CREATE, PERMISSIONS.TOOLS_CONFIGURE]}>
              {canUseCreateTool ? (
                <Suspense fallback={null}>
                  <ToolForgeApp />
                </Suspense>
              ) : (
                  <Navigate to="/tools" replace />
              )}
            </RequirePermission>
          }
        />
        <Route path="config" element={<RequirePermission permission={PERMISSIONS.CONFIG_FEATURE_FLAGS_MANAGE}><ConfigPage /></RequirePermission>} />
        <Route path="config/branding" element={<RequirePermission permission={PERMISSIONS.CONFIG_BRANDING_MANAGE}><BrandingConfigPage /></RequirePermission>} />
        <Route path="needlepath" element={<RequirePermission anyOf={PLATFORM_TELEMETRY_PERMISSIONS}><NeedlepathDashboard /></RequirePermission>} />
        <Route path="mnemo" element={<RequirePermission anyOf={PLATFORM_TELEMETRY_PERMISSIONS}><MnemoDashboard /></RequirePermission>} />
        <Route path="/kb/code-indexer" element={<RequirePermission permission={PERMISSIONS.KNOWLEDGE_CREATE}><CodeIndexerPage /></RequirePermission>} />
        <Route path="/kb/document-indexer" element={<RequirePermission permission={PERMISSIONS.KNOWLEDGE_CREATE}><DocumentIndexerPage /></RequirePermission>} />
        <Route path="/kb" element={<RequirePermission permission={PERMISSIONS.KNOWLEDGE_READ}><KnowledgeBuilderListPage /></RequirePermission>} />
        <Route path="/kb/:type/:id" element={<RequirePermission permission={PERMISSIONS.KNOWLEDGE_READ}><KnowledgeBuilderDetailPage /></RequirePermission>} />
        <Route path="/audit-logs" element={<RequirePermission permission={PERMISSIONS.AUDIT_READ}><AuditLogsPage /></RequirePermission>} />
        <Route path="/add-user" element={<RequirePermission permission={PERMISSIONS.USERS_MANAGE}><AddUserPage /></RequirePermission>} />
        <Route path="/users" element={<RequirePermission permission={PERMISSIONS.USERS_READ}><UserListPage /></RequirePermission>} />
        <Route path="/roles" element={<RequirePermission permission={PERMISSIONS.ROLES_READ}><RolesPermissionsPage /></RequirePermission>} />
        <Route path="/key-manager" element={<RequirePermission permission={PERMISSIONS.CONFIG_KEYS_MANAGE}><KeyManager /></RequirePermission>} />
        {flags.enableWorkflows && (
          <>
            <Route path="/workflows" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_READ}><WorkflowListPage /></RequirePermission>} />
            <Route path="/workflow/generate" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_CREATE}><WorkflowGeneratorPage /></RequirePermission>} />
            <Route path="/workflow/intent-build" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_CREATE}><IntentToWorkflowCreator /></RequirePermission>} />
            <Route path="/workflow/:workflowId/intent-edit" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_UPDATE}><IntentToWorkflowCreator /></RequirePermission>} />
            <Route path="/workflow/approvals" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_APPROVE}><ApprovalsListPage /></RequirePermission>} />
            <Route path="/workflow/approvals/:approvalId" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_APPROVE}><ApprovalDetailPage /></RequirePermission>} />
            <Route path="/workflow/:workflowId/run" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_RUN}><WorkflowRunPage /></RequirePermission>} />
            <Route path="/workflow/:workflowId/edit" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_UPDATE}><WorkflowEditPage /></RequirePermission>} />
            <Route path="/workflow/:workflowId" element={<RequirePermission permission={PERMISSIONS.WORKFLOWS_READ}><WorkflowDetailPage /></RequirePermission>} />
          </>
        )}
      </Route>
    </Routes>
  );
}

function ThemedAppShell() {
  const { flags } = useFeatureFlags();

  return (
    <TenantBrandingProvider whiteLabelDemo={Boolean(flags.whiteLabelDemo)}>
      <BrandedThemeShell />
    </TenantBrandingProvider>
  );
}

function BrandedThemeShell() {
  const { branding } = useTenantBranding();

  return (
    <NextMocaThemeProvider branding={branding}>
      <AppRoutes />
    </NextMocaThemeProvider>
  );
}

export default function App() {
  return (
    <FeatureFlagProvider>
      <UserProvider>
        <BrowserRouter>
          <ThemedAppShell />
        </BrowserRouter>
      </UserProvider>
    </FeatureFlagProvider>
  );
}
